import { useState, useEffect, useRef, useCallback } from "react";
import { Download, Crosshair, ChevronLeft, ChevronRight, X, PanelRightClose, PanelRightOpen, ZoomIn, ZoomOut, Eraser, Undo2 } from "lucide-react";
import { Button } from "./ui/button";
import { toast } from "sonner";

// ── Pipeline helpers ──────────────────────────────────────────────────────────

// Step 5: Reduce yellow saturation (hue 40–75°)
function applyYellowReduction(pixels, amount) {
  if (amount === 0) return;
  const hue2rgb = (p, q, t) => {
    if (t < 0) t += 1; if (t > 1) t -= 1;
    if (t < 1 / 6) return p + (q - p) * 6 * t;
    if (t < 1 / 2) return q;
    if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6;
    return p;
  };
  for (let i = 0; i < pixels.length; i += 4) {
    if (pixels[i + 3] === 0) continue;
    const r = pixels[i] / 255, g = pixels[i + 1] / 255, b = pixels[i + 2] / 255;
    const max = Math.max(r, g, b), min = Math.min(r, g, b), d = max - min;
    if (d === 0) continue;
    let h;
    if (max === r) h = ((g - b) / d + 6) % 6;
    else if (max === g) h = (b - r) / d + 2;
    else h = (r - g) / d + 4;
    const hDeg = h * 60;
    if (hDeg < 40 || hDeg > 75) continue;
    const l = (max + min) / 2;
    const s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    const newS = Math.max(0, s - amount / 100);
    const q2 = l < 0.5 ? l * (1 + newS) : l + newS - l * newS;
    const p2 = 2 * l - q2;
    const hn = h / 6;
    pixels[i]     = Math.round(hue2rgb(p2, q2, hn + 1 / 3) * 255);
    pixels[i + 1] = Math.round(hue2rgb(p2, q2, hn) * 255);
    pixels[i + 2] = Math.round(hue2rgb(p2, q2, hn - 1 / 3) * 255);
  }
}

// Step 7: Boost red saturation (hue 0–20° and 340–360°)
function applyRedBoost(pixels, amount) {
  if (amount === 0) return;
  const hue2rgb = (p, q, t) => {
    if (t < 0) t += 1; if (t > 1) t -= 1;
    if (t < 1 / 6) return p + (q - p) * 6 * t;
    if (t < 1 / 2) return q;
    if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6;
    return p;
  };
  for (let i = 0; i < pixels.length; i += 4) {
    if (pixels[i + 3] === 0) continue;
    const r = pixels[i] / 255, g = pixels[i + 1] / 255, b = pixels[i + 2] / 255;
    const max = Math.max(r, g, b), min = Math.min(r, g, b), d = max - min;
    if (d === 0) continue;
    let h;
    if (max === r) h = ((g - b) / d + 6) % 6;
    else if (max === g) h = (b - r) / d + 2;
    else h = (r - g) / d + 4;
    const hDeg = h * 60;
    if (hDeg > 20 && hDeg < 340) continue; // only reds
    const l = (max + min) / 2;
    const s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    const newS = Math.min(1, s + amount / 100);
    const q2 = l < 0.5 ? l * (1 + newS) : l + newS - l * newS;
    const p2 = 2 * l - q2;
    const hn = h / 6;
    pixels[i]     = Math.round(hue2rgb(p2, q2, hn + 1 / 3) * 255);
    pixels[i + 1] = Math.round(hue2rgb(p2, q2, hn) * 255);
    pixels[i + 2] = Math.round(hue2rgb(p2, q2, hn - 1 / 3) * 255);
  }
}

// Step 2 extra: Boost lightness (clean up paper tone)
function applyLightness(pixels, amount) {
  if (amount === 0) return;
  const add = Math.round(amount * 2.55);
  for (let i = 0; i < pixels.length; i += 4) {
    if (pixels[i + 3] === 0) continue;
    pixels[i]     = Math.min(255, pixels[i]     + add);
    pixels[i + 1] = Math.min(255, pixels[i + 1] + add);
    pixels[i + 2] = Math.min(255, pixels[i + 2] + add);
  }
}

// Steps 3 & 8: Grain — Overlay blend with 50% grey noise layer
function applyGrain(pixels, amount) {
  if (amount === 0) return;
  const dev = amount / 100;
  for (let i = 0; i < pixels.length; i += 4) {
    if (pixels[i + 3] === 0) continue;
    const noise = 0.5 + (Math.random() - 0.5) * dev * 2;
    for (let c = 0; c < 3; c++) {
      const base = pixels[i + c] / 255;
      const blended = base < 0.5
        ? 2 * base * noise
        : 1 - 2 * (1 - base) * (1 - noise);
      pixels[i + c] = Math.max(0, Math.min(255, Math.round(blended * 255)));
    }
  }
}

// Auto Cleanup: despill background color from semi-transparent edge pixels + remove strays
function applyCleanup(data, sw, sh, bgColor, strength) {
  const { r: br, g: bg, b: bb } = bgColor;
  const s = strength / 100;

  // Pass 1: reduce alpha and despill color for pixels similar to the background
  for (let i = 0; i < data.length; i += 4) {
    const a = data[i + 3];
    if (a === 0 || a === 255) continue;
    const r = data[i], g = data[i + 1], b = data[i + 2];
    const dist = Math.sqrt((r - br) ** 2 + (g - bg) ** 2 + (b - bb) ** 2);
    const closeness = Math.max(0, 1 - dist / 441.67); // 441.67 = sqrt(3*255^2)
    data[i + 3] = Math.round(a * (1 - closeness * s * 0.95));
    if (data[i + 3] > 0 && closeness > 0.1) {
      const push = closeness * s * 0.5;
      data[i]     = Math.min(255, Math.max(0, Math.round(r + (r - br) * push)));
      data[i + 1] = Math.min(255, Math.max(0, Math.round(g + (g - bg) * push)));
      data[i + 2] = Math.min(255, Math.max(0, Math.round(b + (b - bb) * push)));
    }
  }

  // Pass 2: remove isolated stray pixels (too few visible neighbors)
  if (s > 0.2) {
    const snapshot = new Uint8ClampedArray(data);
    const minNeighbors = Math.round(2 + s * 3);
    for (let y = 1; y < sh - 1; y++) {
      for (let x = 1; x < sw - 1; x++) {
        const i = (y * sw + x) * 4;
        if (snapshot[i + 3] < 5) continue;
        let count = 0;
        for (let dy = -1; dy <= 1; dy++) {
          for (let dx = -1; dx <= 1; dx++) {
            if (!dx && !dy) continue;
            if (snapshot[((y + dy) * sw + (x + dx)) * 4 + 3] > 5) count++;
          }
        }
        if (count < minNeighbors) data[i + 3] = 0;
      }
    }
  }
}

// Step 8: Feather edges — separable box blur on alpha channel
function applyFeather(outData, sw, sh, radius) {
  const r = Math.round(radius);
  if (r <= 0) return;
  const alpha = new Float32Array(sw * sh);
  for (let i = 0; i < sw * sh; i++) alpha[i] = outData.data[i * 4 + 3];
  const temp = new Float32Array(sw * sh);
  // Horizontal
  for (let y = 0; y < sh; y++) {
    for (let x = 0; x < sw; x++) {
      let sum = 0, cnt = 0;
      for (let dx = -r; dx <= r; dx++) {
        const nx = x + dx;
        if (nx >= 0 && nx < sw) { sum += alpha[y * sw + nx]; cnt++; }
      }
      temp[y * sw + x] = sum / cnt;
    }
  }
  // Vertical
  for (let y = 0; y < sh; y++) {
    for (let x = 0; x < sw; x++) {
      let sum = 0, cnt = 0;
      for (let dy = -r; dy <= r; dy++) {
        const ny = y + dy;
        if (ny >= 0 && ny < sh) { sum += temp[ny * sw + x]; cnt++; }
      }
      outData.data[(y * sw + x) * 4 + 3] = Math.round(sum / cnt);
    }
  }
}

// ── Persistence helpers ──────────────────────────────────────────────────────

function koKey(imageId, type) { return `ko_${type}_${imageId}`; }

function saveSettingsToStorage(imageId, settings) {
  try { localStorage.setItem(koKey(imageId, 'settings'), JSON.stringify(settings)); } catch {}
}

function loadSettingsFromStorage(imageId) {
  try { return JSON.parse(localStorage.getItem(koKey(imageId, 'settings'))); } catch { return null; }
}

function saveMaskToStorage(imageId, mask, w, h) {
  if (!mask || !imageId) return;
  try {
    const p = [];
    for (let i = 0; i < mask.length; i++) { if (mask[i]) p.push(i); }
    localStorage.setItem(koKey(imageId, 'mask'), JSON.stringify({ v: 1, w, h, p }));
  } catch {}
}

function loadMaskFromStorage(imageId, w, h) {
  try {
    const raw = localStorage.getItem(koKey(imageId, 'mask'));
    if (!raw) return null;
    const { v, w: sw, h: sh, p } = JSON.parse(raw);
    if (v !== 1 || sw !== w || sh !== h) return null;
    const mask = new Uint8Array(w * h);
    for (const idx of p) { if (idx >= 0 && idx < mask.length) mask[idx] = 1; }
    return mask;
  } catch { return null; }
}

function clearMaskFromStorage(imageId) {
  try { localStorage.removeItem(koKey(imageId, 'mask')); } catch {}
}

function saveHistoryEntry(imageId, settings) {
  try {
    const key = koKey(imageId, 'history');
    const existing = JSON.parse(localStorage.getItem(key) || '[]');
    existing.push({ ts: Date.now(), settings });
    localStorage.setItem(key, JSON.stringify(existing.slice(-8)));
  } catch {}
}

function loadHistory(imageId) {
  try { return JSON.parse(localStorage.getItem(koKey(imageId, 'history')) || '[]'); } catch { return []; }
}

function clearAllStorage(imageId) {
  ['settings', 'mask', 'history'].forEach((t) => { try { localStorage.removeItem(koKey(imageId, t)); } catch {} });
}

function timeAgo(ts) {
  const s = Math.floor((Date.now() - ts) / 1000);
  if (s < 60) return 'just now';
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
}

const DEFAULT_SETTINGS = {
  knockoutColor: { r: 255, g: 255, b: 255, hex: '#ffffff' },
  knockoutTolerance: 80, dotFrequency: 8, dotAngle: 45, dotPattern: 'dots',
  edgeFeather: 0, yellowReduction: 0, lightness: 0, redBoost: 0, grain: 0,
  autoCleanup: false, cleanupStrength: 60,
};

// Works in two modes:
//   embedded={false} (default) — fixed full-screen overlay (used from Winners modal)
//   embedded={true}            — fills its container (used from Knockout page)

export default function KnockoutWorkspace({ image, onClose, embedded = false }) {
  const [eyedropperActive, setEyedropperActive] = useState(false);
  const [knockoutColor, setKnockoutColor] = useState({ r: 255, g: 255, b: 255, hex: "#ffffff" });
  const [knockoutTolerance, setKnockoutTolerance] = useState(80);
  const [dotFrequency, setDotFrequency] = useState(8);
  const [dotAngle, setDotAngle] = useState(45);
  const [dotPattern, setDotPattern] = useState("dots");
  const [edgeFeather, setEdgeFeather] = useState(0);
  const [yellowReduction, setYellowReduction] = useState(0);
  const [lightness, setLightness] = useState(0);
  const [redBoost, setRedBoost] = useState(0);
  const [grain, setGrain] = useState(0);
  const [autoCleanup, setAutoCleanup] = useState(false);
  const [cleanupStrength, setCleanupStrength] = useState(60);
  const [sourceReady, setSourceReady] = useState(false);
  const [history, setHistory] = useState([]);
  const [historyOpen, setHistoryOpen] = useState(false);
  const imageIdRef = useRef(image?.id);
  const settingsSaveTimerRef = useRef(null);
  const lastHistoryTsRef = useRef(0);
  const [sliderPos, setSliderPos] = useState(50);
  const [displayBounds, setDisplayBounds] = useState({ x: 0, y: 0, w: 0, h: 0 });
  const [panelOpen, setPanelOpen] = useState(true);

  const knockoutCanvasRef = useRef(null);
  const sourceCanvasRef = useRef(null);
  const containerRef = useRef(null);
  const isDraggingRef = useRef(false);

  // Zoom / pan
  const [zoom, setZoom] = useState(1);
  const [panOffset, setPanOffset] = useState({ x: 0, y: 0 });
  const zoomRef = useRef(1);
  const panRef = useRef({ x: 0, y: 0 });
  const isPanningRef = useRef(false);
  const panStartRef = useRef({ mx: 0, my: 0, px: 0, py: 0 });
  const hasPannedRef = useRef(false);

  // Eraser
  const [eraserActive, setEraserActive] = useState(false);
  const [eraserSize, setEraserSize] = useState(30);
  const [undoStack, setUndoStack] = useState([]);
  const [eraserPos, setEraserPos] = useState(null);
  const eraseMaskRef = useRef(null);
  const cleanRenderRef = useRef(null);
  const isErasingRef = useRef(false);
  const lastErasePointRef = useRef(null);

  // ── Image loading ──
  // fetch+blob with stable cache-buster (?_c=<image.id>) so Cloudflare CDN
  // is forced to go to the Worker for this URL (cache miss on first load).
  // The Worker always returns Access-Control-Allow-Origin, so the fetch
  // succeeds cross-origin. The blob URL is same-origin so the canvas is
  // never tainted. No silent fallback that taints the canvas.
  useEffect(() => {
    if (!image?.image_data) return;
    setSourceReady(false);
    let blobUrl = null;
    let active = true;

    const sep = image.image_data.includes("?") ? "&" : "?";
    const corsUrl = `${image.image_data}${sep}_c=${image.id}`;

    fetch(corsUrl, { mode: "cors" })
      .then((r) => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return r.blob();
      })
      .then((blob) => {
        if (!active) return;
        blobUrl = URL.createObjectURL(blob);
        const img = new Image();
        img.onload = () => {
          if (!active) return;
          URL.revokeObjectURL(blobUrl);
          blobUrl = null;
          const sc = sourceCanvasRef.current;
          if (!sc) return;
          sc.width = img.naturalWidth;
          sc.height = img.naturalHeight;
          sc.getContext("2d").drawImage(img, 0, 0);
          // Restore erase mask
          const savedMask = loadMaskFromStorage(image.id, img.naturalWidth, img.naturalHeight);
          eraseMaskRef.current = savedMask || new Uint8Array(img.naturalWidth * img.naturalHeight);
          cleanRenderRef.current = null;
          setUndoStack([]);
          // Restore settings
          const saved = loadSettingsFromStorage(image.id);
          if (saved) {
            if (saved.knockoutColor) setKnockoutColor(saved.knockoutColor);
            if (saved.knockoutTolerance != null) setKnockoutTolerance(saved.knockoutTolerance);
            if (saved.dotFrequency != null) setDotFrequency(saved.dotFrequency);
            if (saved.dotAngle != null) setDotAngle(saved.dotAngle);
            if (saved.dotPattern) setDotPattern(saved.dotPattern);
            if (saved.edgeFeather != null) setEdgeFeather(saved.edgeFeather);
            if (saved.yellowReduction != null) setYellowReduction(saved.yellowReduction);
            if (saved.lightness != null) setLightness(saved.lightness);
            if (saved.redBoost != null) setRedBoost(saved.redBoost);
            if (saved.grain != null) setGrain(saved.grain);
            if (saved.autoCleanup != null) setAutoCleanup(saved.autoCleanup);
            if (saved.cleanupStrength != null) setCleanupStrength(saved.cleanupStrength);
          }
          // Load history
          imageIdRef.current = image.id;
          setHistory(loadHistory(image.id));
          lastHistoryTsRef.current = 0;
          setSourceReady(true);
        };
        img.src = blobUrl;
      })
      .catch((err) => {
        if (!active) return;
        toast.error(`Could not load image: ${err?.message ?? "network error"}`);
      });

    return () => {
      active = false;
      if (blobUrl) URL.revokeObjectURL(blobUrl);
    };
  }, [image?.id]);

  // Reset zoom when image changes
  useEffect(() => {
    zoomRef.current = 1;
    panRef.current = { x: 0, y: 0 };
    setZoom(1);
    setPanOffset({ x: 0, y: 0 });
  }, [image?.id]);

  // ── Display bounds (object-contain) ──
  const updateBounds = useCallback(() => {
    const container = containerRef.current;
    const sc = sourceCanvasRef.current;
    if (!container || !sc || !sc.width) return;
    const cw = container.clientWidth;
    const ch = container.clientHeight;
    const scale = Math.min(cw / sc.width, ch / sc.height);
    const w = sc.width * scale;
    const h = sc.height * scale;
    setDisplayBounds({ x: (cw - w) / 2, y: (ch - h) / 2, w, h });
  }, []);

  useEffect(() => {
    if (!sourceReady) return;
    updateBounds();
    const ro = new ResizeObserver(updateBounds);
    if (containerRef.current) ro.observe(containerRef.current);
    return () => ro.disconnect();
  }, [sourceReady, updateBounds]);


  const hexToRgb = (hex) => {
    const r = parseInt(hex.slice(1, 3), 16);
    const g = parseInt(hex.slice(3, 5), 16);
    const b = parseInt(hex.slice(5, 7), 16);
    return { r, g, b, hex };
  };

  // ── Knockout algorithm ──
  const processKnockout = useCallback(() => {
    const sc = sourceCanvasRef.current;
    const oc = knockoutCanvasRef.current;
    if (!sc || !oc || !sourceReady) return;

    const sw = sc.width, sh = sc.height;
    oc.width = sw;
    oc.height = sh;
    const ctx = oc.getContext("2d");
    ctx.clearRect(0, 0, sw, sh);

    let srcData;
    try {
      srcData = sc.getContext("2d").getImageData(0, 0, sw, sh);
    } catch {
      ctx.drawImage(sc, 0, 0);
      return;
    }

    // Pre-process on a working copy (Steps 2, 5, 7) — source canvas stays clean
    const pixels = new Uint8ClampedArray(srcData.data);
    applyLightness(pixels, lightness);
    applyYellowReduction(pixels, yellowReduction);
    applyRedBoost(pixels, redBoost);

    const { r: tr, g: tg, b: tb } = knockoutColor;

    // Pass 1: colour knockout
    const outData = ctx.createImageData(sw, sh);
    for (let i = 0; i < pixels.length; i += 4) {
      const r = pixels[i], g = pixels[i + 1], b = pixels[i + 2], a = pixels[i + 3];
      if (a === 255) {
        const dist = Math.sqrt((r - tr) ** 2 + (g - tg) ** 2 + (b - tb) ** 2);
        if (dist >= knockoutTolerance) {
          outData.data[i] = r; outData.data[i + 1] = g; outData.data[i + 2] = b; outData.data[i + 3] = 255;
        }
      }
    }

    // Auto Cleanup: despill + stray pixel removal (before feather)
    if (autoCleanup) applyCleanup(outData.data, sw, sh, knockoutColor, cleanupStrength);

    // Step 8: edge feathering — blur alpha before putting to canvas
    if (edgeFeather > 0) applyFeather(outData, sw, sh, edgeFeather);

    ctx.putImageData(outData, 0, 0);

    // Pass 2: halftone for semi-transparent source pixels (original behaviour)
    const spacing = Math.max(3, Math.round(100 / dotFrequency));
    const angleRad = (dotAngle * Math.PI) / 180;
    const cosA = Math.cos(angleRad), sinA = Math.sin(angleRad);
    const diag = Math.ceil(Math.sqrt(sw * sw + sh * sh));

    for (let gy = -diag; gy < diag; gy += spacing) {
      for (let gx = -diag; gx < diag; gx += spacing) {
        const cx = Math.round(cosA * gx - sinA * gy + sw / 2);
        const cy = Math.round(sinA * gx + cosA * gy + sh / 2);
        if (cx < 0 || cx >= sw || cy < 0 || cy >= sh) continue;
        const half = Math.floor(spacing / 2);
        let rS = 0, gS = 0, bS = 0, aS = 0, n = 0;
        for (let dy = -half; dy <= half; dy++) {
          for (let dx = -half; dx <= half; dx++) {
            const px = cx + dx, py = cy + dy;
            if (px < 0 || px >= sw || py < 0 || py >= sh) continue;
            const idx = (py * sw + px) * 4;
            const a = srcData.data[idx + 3];
            if (a > 0 && a < 255) { rS += srcData.data[idx]; gS += srcData.data[idx + 1]; bS += srcData.data[idx + 2]; aS += a; n++; }
          }
        }
        if (n === 0) continue;
        const radius = (spacing / 2) * (aS / n / 255);
        if (radius < 0.3) continue;
        ctx.fillStyle = `rgb(${Math.round(rS / n)},${Math.round(gS / n)},${Math.round(bS / n)})`;
        if (dotPattern === "dots") {
          ctx.beginPath(); ctx.arc(cx, cy, radius, 0, Math.PI * 2); ctx.fill();
        } else {
          ctx.save(); ctx.translate(cx, cy); ctx.rotate(angleRad);
          ctx.fillRect(-radius, -spacing / 2, radius * 2, spacing); ctx.restore();
        }
      }
    }
    // Steps 3 & 8: grain as final overlay pass
    if (grain > 0) {
      const result = ctx.getImageData(0, 0, sw, sh);
      applyGrain(result.data, grain);
      ctx.putImageData(result, 0, 0);
    }

    // Save clean render and re-apply erase mask
    const finalData = ctx.getImageData(0, 0, sw, sh);
    cleanRenderRef.current = finalData;
    const mask = eraseMaskRef.current;
    if (mask) {
      const masked = new ImageData(new Uint8ClampedArray(finalData.data), sw, sh);
      for (let i = 0; i < mask.length; i++) { if (mask[i]) masked.data[i * 4 + 3] = 0; }
      ctx.putImageData(masked, 0, 0);
    }
  }, [knockoutColor, knockoutTolerance, dotFrequency, dotAngle, dotPattern, edgeFeather, yellowReduction, lightness, redBoost, grain, autoCleanup, cleanupStrength, sourceReady]);

  // Auto-run on load and whenever controls change
  useEffect(() => {
    if (sourceReady) processKnockout();
  }, [processKnockout, sourceReady]);

  // Auto-save settings + auto-snapshot history on change
  useEffect(() => {
    if (!sourceReady || !imageIdRef.current) return;
    const settings = { knockoutColor, knockoutTolerance, dotFrequency, dotAngle, dotPattern, edgeFeather, yellowReduction, lightness, redBoost, grain, autoCleanup, cleanupStrength };
    clearTimeout(settingsSaveTimerRef.current);
    settingsSaveTimerRef.current = setTimeout(() => {
      saveSettingsToStorage(imageIdRef.current, settings);
      // Auto-snapshot if 2 minutes have passed since last snapshot
      if (Date.now() - lastHistoryTsRef.current > 120000) {
        lastHistoryTsRef.current = Date.now();
        saveHistoryEntry(imageIdRef.current, settings);
        setHistory(loadHistory(imageIdRef.current));
      }
    }, 800);
    return () => clearTimeout(settingsSaveTimerRef.current);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [knockoutColor, knockoutTolerance, dotFrequency, dotAngle, dotPattern, edgeFeather, yellowReduction, lightness, redBoost, grain, autoCleanup, cleanupStrength, sourceReady]);

  // ── Slider drag ──
  const updateSlider = useCallback((clientX) => {
    if (!containerRef.current || displayBounds.w === 0) return;
    const rect = containerRef.current.getBoundingClientRect();
    const x = (clientX - rect.left) / zoomRef.current - panRef.current.x - displayBounds.x;
    const clamped = Math.max(0, Math.min(x, displayBounds.w));
    setSliderPos((clamped / displayBounds.w) * 100);
  }, [displayBounds]);

  // ── Eraser ──
  const doErase = useCallback((clientX, clientY) => {
    const canvas = knockoutCanvasRef.current;
    const container = containerRef.current;
    if (!canvas || !container || !eraseMaskRef.current || !cleanRenderRef.current) return;
    const rect = container.getBoundingClientRect();
    const contX = (clientX - rect.left) / zoomRef.current - panRef.current.x;
    const contY = (clientY - rect.top) / zoomRef.current - panRef.current.y;
    const sw = canvas.width, sh = canvas.height;
    const cx = (contX - displayBounds.x) / displayBounds.w * sw;
    const cy = (contY - displayBounds.y) / displayBounds.h * sh;
    const r = eraserSize * (sw / displayBounds.w);
    const r2 = r * r;
    const x0 = Math.max(0, Math.floor(cx - r));
    const x1 = Math.min(sw - 1, Math.ceil(cx + r));
    const y0 = Math.max(0, Math.floor(cy - r));
    const y1 = Math.min(sh - 1, Math.ceil(cy + r));
    const mask = eraseMaskRef.current;
    for (let py = y0; py <= y1; py++) {
      for (let px = x0; px <= x1; px++) {
        const dx = px - cx, dy = py - cy;
        if (dx * dx + dy * dy <= r2) mask[py * sw + px] = 1;
      }
    }
    const ctx = canvas.getContext("2d");
    const masked = new ImageData(new Uint8ClampedArray(cleanRenderRef.current.data), sw, sh);
    for (let i = 0; i < mask.length; i++) { if (mask[i]) masked.data[i * 4 + 3] = 0; }
    ctx.putImageData(masked, 0, 0);
  }, [displayBounds, eraserSize]);

  const undo = useCallback(() => {
    setUndoStack((prev) => {
      if (prev.length === 0) return prev;
      const snapshot = prev[prev.length - 1];
      eraseMaskRef.current = new Uint8Array(snapshot);
      const canvas = knockoutCanvasRef.current;
      if (canvas && cleanRenderRef.current) {
        const sw = canvas.width, sh = canvas.height;
        const ctx = canvas.getContext("2d");
        const masked = new ImageData(new Uint8ClampedArray(cleanRenderRef.current.data), sw, sh);
        const mask = eraseMaskRef.current;
        for (let i = 0; i < mask.length; i++) { if (mask[i]) masked.data[i * 4 + 3] = 0; }
        ctx.putImageData(masked, 0, 0);
      }
      return prev.slice(0, -1);
    });
  }, []);

  // Ctrl+Z undo
  useEffect(() => {
    const onKey = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key === "z" && !e.shiftKey) {
        e.preventDefault();
        undo();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [undo]);

  useEffect(() => {
    const onMove = (e) => {
      const clientX = e.clientX ?? e.touches?.[0]?.clientX;
      const clientY = e.clientY ?? e.touches?.[0]?.clientY;
      if (isDraggingRef.current) updateSlider(clientX);
      if (isPanningRef.current) {
        const dx = e.clientX - panStartRef.current.mx;
        const dy = e.clientY - panStartRef.current.my;
        if (Math.abs(dx) > 2 || Math.abs(dy) > 2) hasPannedRef.current = true;
        const newX = panStartRef.current.px + dx / zoomRef.current;
        const newY = panStartRef.current.py + dy / zoomRef.current;
        panRef.current = { x: newX, y: newY };
        setPanOffset({ x: newX, y: newY });
      }
      if (isErasingRef.current && clientX != null && clientY != null) {
        const last = lastErasePointRef.current;
        if (last) {
          const dx = clientX - last.x, dy = clientY - last.y;
          const dist = Math.sqrt(dx * dx + dy * dy);
          const step = Math.max(2, eraserSize / 3);
          if (dist >= step) {
            const steps = Math.ceil(dist / step);
            for (let i = 1; i <= steps; i++) doErase(last.x + dx * i / steps, last.y + dy * i / steps);
            lastErasePointRef.current = { x: clientX, y: clientY };
          }
        } else {
          doErase(clientX, clientY);
          lastErasePointRef.current = { x: clientX, y: clientY };
        }
      }
    };
    const onUp = () => {
      isDraggingRef.current = false;
      isPanningRef.current = false;
      if (isErasingRef.current) {
        const canvas = knockoutCanvasRef.current;
        if (canvas) saveMaskToStorage(imageIdRef.current, eraseMaskRef.current, canvas.width, canvas.height);
      }
      isErasingRef.current = false;
      lastErasePointRef.current = null;
    };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
    window.addEventListener("touchmove", onMove, { passive: true });
    window.addEventListener("touchend", onUp);
    return () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
      window.removeEventListener("touchmove", onMove);
      window.removeEventListener("touchend", onUp);
    };
  }, [updateSlider, doErase, eraserSize]);

  // ── Zoom helpers ──
  const applyZoom = useCallback((newZoom, mx, my) => {
    newZoom = Math.max(1, Math.min(8, newZoom));
    const newX = panRef.current.x + mx * (1 / newZoom - 1 / zoomRef.current);
    const newY = panRef.current.y + my * (1 / newZoom - 1 / zoomRef.current);
    const finalX = newZoom === 1 ? 0 : newX;
    const finalY = newZoom === 1 ? 0 : newY;
    zoomRef.current = newZoom;
    panRef.current = { x: finalX, y: finalY };
    setZoom(newZoom);
    setPanOffset({ x: finalX, y: finalY });
  }, []);

  // Wheel zoom (non-passive so we can preventDefault)
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const onWheel = (e) => {
      e.preventDefault();
      const rect = el.getBoundingClientRect();
      const mx = e.clientX - rect.left;
      const my = e.clientY - rect.top;
      const delta = e.deltaY > 0 ? -0.5 : 0.5;
      applyZoom(zoomRef.current + delta, mx, my);
    };
    el.addEventListener("wheel", onWheel, { passive: false });
    return () => el.removeEventListener("wheel", onWheel);
  }, [applyZoom]);

  // Escape closes (overlay mode only)
  useEffect(() => {
    if (embedded) return;
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose, embedded]);

  // ── Eyedropper ──
  const handleContainerClick = (e) => {
    if (hasPannedRef.current) { hasPannedRef.current = false; return; }
    if (!eyedropperActive) return;
    const sc = sourceCanvasRef.current;
    if (!sc || !sourceReady || displayBounds.w === 0) return;
    const rect = containerRef.current.getBoundingClientRect();
    const clickX = (e.clientX - rect.left) / zoomRef.current - panRef.current.x - displayBounds.x;
    const clickY = (e.clientY - rect.top) / zoomRef.current - panRef.current.y - displayBounds.y;
    if (clickX < 0 || clickX > displayBounds.w || clickY < 0 || clickY > displayBounds.h) return;
    const px = Math.floor((clickX / displayBounds.w) * sc.width);
    const py = Math.floor((clickY / displayBounds.h) * sc.height);
    try {
      const [r, g, b] = sc.getContext("2d").getImageData(px, py, 1, 1).data;
      const hex = "#" + [r, g, b].map((v) => v.toString(16).padStart(2, "0")).join("");
      setKnockoutColor({ r, g, b, hex });
    } catch {
      toast.error("Cannot sample colour — image is cross-origin");
    }
    setEyedropperActive(false);
  };

  const downloadLayer = () => {
    const canvas = knockoutCanvasRef.current;
    if (!canvas) return;
    const link = document.createElement("a");
    link.href = canvas.toDataURL("image/png");
    link.download = `knockout-${image.id}.png`;
    link.click();
  };

  // Step 10: Export at A3 300 DPI (3508 × 4961 px)
  const downloadA3 = () => {
    const canvas = knockoutCanvasRef.current;
    if (!canvas) return;
    const A3_W = 3508, A3_H = 4961;
    const exp = document.createElement("canvas");
    exp.width = A3_W; exp.height = A3_H;
    const ctx = exp.getContext("2d");
    const scale = Math.min(A3_W / canvas.width, A3_H / canvas.height);
    const w = canvas.width * scale, h = canvas.height * scale;
    ctx.drawImage(canvas, (A3_W - w) / 2, (A3_H - h) / 2, w, h);
    const link = document.createElement("a");
    link.href = exp.toDataURL("image/png");
    link.download = `knockout-a3-${image.id}.png`;
    link.click();
  };

  const reset = () => {
    const d = DEFAULT_SETTINGS;
    setKnockoutColor(d.knockoutColor);
    setKnockoutTolerance(d.knockoutTolerance);
    setDotFrequency(d.dotFrequency);
    setDotAngle(d.dotAngle);
    setDotPattern(d.dotPattern);
    setEdgeFeather(d.edgeFeather);
    setYellowReduction(d.yellowReduction);
    setLightness(d.lightness);
    setRedBoost(d.redBoost);
    setGrain(d.grain);
    setAutoCleanup(d.autoCleanup);
    setCleanupStrength(d.cleanupStrength);
    setEraserActive(false);
    setUndoStack([]);
    if (eraseMaskRef.current) eraseMaskRef.current.fill(0);
    clearAllStorage(imageIdRef.current);
    setHistory([]);
  };

  const saveSnapshot = () => {
    const settings = { knockoutColor, knockoutTolerance, dotFrequency, dotAngle, dotPattern, edgeFeather, yellowReduction, lightness, redBoost, grain, autoCleanup, cleanupStrength };
    lastHistoryTsRef.current = Date.now();
    saveHistoryEntry(imageIdRef.current, settings);
    setHistory(loadHistory(imageIdRef.current));
  };

  const restoreSnapshot = (settings) => {
    if (settings.knockoutColor) setKnockoutColor(settings.knockoutColor);
    if (settings.knockoutTolerance != null) setKnockoutTolerance(settings.knockoutTolerance);
    if (settings.dotFrequency != null) setDotFrequency(settings.dotFrequency);
    if (settings.dotAngle != null) setDotAngle(settings.dotAngle);
    if (settings.dotPattern) setDotPattern(settings.dotPattern);
    if (settings.edgeFeather != null) setEdgeFeather(settings.edgeFeather);
    if (settings.yellowReduction != null) setYellowReduction(settings.yellowReduction);
    if (settings.lightness != null) setLightness(settings.lightness);
    if (settings.redBoost != null) setRedBoost(settings.redBoost);
    if (settings.grain != null) setGrain(settings.grain);
    if (settings.autoCleanup != null) setAutoCleanup(settings.autoCleanup);
    if (settings.cleanupStrength != null) setCleanupStrength(settings.cleanupStrength);
  };

  const clearErases = () => {
    if (!eraseMaskRef.current) return;
    setUndoStack((prev) => [...prev.slice(-19), new Uint8Array(eraseMaskRef.current)]);
    eraseMaskRef.current.fill(0);
    clearMaskFromStorage(imageIdRef.current);
    const canvas = knockoutCanvasRef.current;
    if (canvas && cleanRenderRef.current) canvas.getContext("2d").putImageData(cleanRenderRef.current, 0, 0);
  };

  const sliderHandleX = displayBounds.x + (sliderPos / 100) * displayBounds.w;

  const wrapperClass = embedded
    ? "w-full h-full bg-white flex flex-col"
    : "fixed inset-0 z-[200] bg-white flex flex-col";

  return (
    <div className={wrapperClass}>

      {/* Header */}
      <div className="flex items-center gap-4 px-5 py-3 border-b border-gray-200 flex-shrink-0">
        <button
          onClick={onClose}
          className="flex items-center gap-1.5 text-sm text-gray-600 hover:text-black transition-colors"
        >
          <X size={16} />
          <span>{embedded ? "Back" : "Close"}</span>
        </button>
        <div className="w-px h-5 bg-gray-200" />
        <p className="flex-1 text-sm font-medium truncate text-gray-700">
          {image.title || image.prompt || "Knockout Generator"}
        </p>
        <button onClick={saveSnapshot} className="text-xs text-gray-500 hover:text-black border border-gray-200 hover:border-black px-2 py-1 transition-colors" title="Save settings snapshot to history">
          Snapshot
        </button>
        <button onClick={reset} className="text-xs text-gray-500 hover:text-black underline transition-colors">
          Reset
        </button>
        {undoStack.length > 0 && (
          <button
            onClick={undo}
            className="flex items-center gap-1 text-xs px-2 py-1 border border-gray-200 hover:border-black text-gray-600 hover:text-black transition-colors"
            title="Undo erase (Ctrl+Z)"
          >
            <Undo2 size={13} />
            Undo
          </button>
        )}
        <button
          onClick={() => setEraserActive((v) => !v)}
          title="Eraser tool"
          className={`p-1.5 border transition-colors ${eraserActive ? "bg-yellow-400 border-yellow-500" : "border-gray-200 hover:border-gray-400"}`}
        >
          <Eraser size={14} />
        </button>
        <button
          onClick={() => setPanelOpen((v) => !v)}
          title={panelOpen ? "Hide controls" : "Show controls"}
          className="p-1.5 text-gray-400 hover:text-black transition-colors"
        >
          {panelOpen ? <PanelRightClose size={16} /> : <PanelRightOpen size={16} />}
        </button>
        <Button
          onClick={downloadLayer}
          disabled={!sourceReady}
          size="sm"
          variant="outline"
          className="rounded-none"
        >
          <Download size={13} className="mr-1.5" />
          Download
        </Button>
        <Button
          onClick={downloadA3}
          disabled={!sourceReady}
          size="sm"
          className="rounded-none bg-black text-white hover:bg-gray-800"
        >
          <Download size={13} className="mr-1.5" />
          Export A3
        </Button>
      </div>

      {/* Body */}
      <div className="flex flex-1 overflow-hidden">

        {/* Image / slider area */}
        <div
          ref={containerRef}
          className={`flex-1 relative overflow-hidden bg-checkerboard select-none ${eraserActive ? "cursor-none" : eyedropperActive ? "cursor-crosshair" : zoom > 1 ? "cursor-grab" : ""}`}
          onClick={handleContainerClick}
          onMouseMove={(e) => {
            if (eraserActive) {
              const rect = containerRef.current.getBoundingClientRect();
              setEraserPos({ x: e.clientX - rect.left, y: e.clientY - rect.top });
            }
          }}
          onMouseLeave={() => setEraserPos(null)}
          onMouseDown={(e) => {
            if (eraserActive && sourceReady) {
              if (eraseMaskRef.current) {
                setUndoStack((prev) => [...prev.slice(-19), new Uint8Array(eraseMaskRef.current)]);
              }
              isErasingRef.current = true;
              lastErasePointRef.current = null;
              doErase(e.clientX, e.clientY);
              return;
            }
            if (isDraggingRef.current || eyedropperActive || zoomRef.current <= 1) return;
            isPanningRef.current = true;
            hasPannedRef.current = false;
            panStartRef.current = { mx: e.clientX, my: e.clientY, px: panRef.current.x, py: panRef.current.y };
          }}
        >
          {!sourceReady && (
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="w-8 h-8 border-2 border-black border-t-transparent rounded-full animate-spin" />
            </div>
          )}

          {/* Transform wrapper for zoom/pan */}
          <div style={{
            position: "absolute", inset: 0,
            transform: `scale(${zoom}) translate(${panOffset.x}px, ${panOffset.y}px)`,
            transformOrigin: "0 0",
          }}>
            {/* Canvases always in DOM so refs are valid when processKnockout fires */}
            <canvas ref={sourceCanvasRef} style={{ display: "none" }} />
            {/* Knockout canvas — always mounted so ref is valid; sits above original img */}
            <canvas
              ref={knockoutCanvasRef}
              style={{
                position: "absolute",
                display: displayBounds.w > 0 ? "block" : "none",
                left: displayBounds.x, top: displayBounds.y,
                width: displayBounds.w, height: displayBounds.h,
                clipPath: `inset(0 ${100 - sliderPos}% 0 0)`,
                zIndex: 2,
              }}
            />

            {displayBounds.w > 0 && (
              <>
                {/* Original — clipped to RIGHT of slider only */}
                <img
                  src={image.image_data}
                  alt="Original"
                  draggable={false}
                  style={{
                    position: "absolute",
                    left: displayBounds.x, top: displayBounds.y,
                    width: displayBounds.w, height: displayBounds.h,
                    userSelect: "none",
                    clipPath: `inset(0 0 0 ${sliderPos}%)`,
                  }}
                />

                {/* Slider line + handle */}
                <div
                  style={{
                    position: "absolute",
                    left: sliderHandleX,
                    top: displayBounds.y,
                    height: displayBounds.h,
                    transform: "translateX(-50%)",
                    width: 40,
                    display: "flex", flexDirection: "column", alignItems: "center",
                    cursor: "ew-resize", zIndex: 10,
                  }}
                  onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); isDraggingRef.current = true; }}
                  onTouchStart={(e) => { e.stopPropagation(); isDraggingRef.current = true; }}
                >
                  <div style={{ width: 2, flex: 1, background: "#111", boxShadow: "0 0 6px rgba(0,0,0,0.3)" }} />
                  <div style={{
                    position: "absolute", top: "50%", transform: "translateY(-50%)",
                    width: 32, height: 32, background: "white", borderRadius: "50%",
                    border: "1px solid #d1d5db", boxShadow: "0 2px 8px rgba(0,0,0,0.2)",
                    display: "flex", alignItems: "center", justifyContent: "center",
                  }}>
                    <ChevronLeft size={10} className="text-gray-500 -mr-0.5" />
                    <ChevronRight size={10} className="text-gray-500 -ml-0.5" />
                  </div>
                </div>

                {/* Labels */}
                <div className="absolute px-2 py-0.5 text-[10px] uppercase tracking-widest text-white bg-black/50 pointer-events-none"
                  style={{ left: displayBounds.x + 10, top: displayBounds.y + 10 }}>
                  Knockout
                </div>
                <div className="absolute px-2 py-0.5 text-[10px] uppercase tracking-widest text-white bg-black/50 pointer-events-none"
                  style={{ left: displayBounds.x + displayBounds.w - 78, top: displayBounds.y + 10 }}>
                  Original
                </div>
              </>
            )}
          </div>

          {/* Eraser brush cursor */}
          {eraserActive && eraserPos && (
            <div style={{
              position: "absolute",
              left: eraserPos.x, top: eraserPos.y,
              width: eraserSize * 2, height: eraserSize * 2,
              transform: "translate(-50%, -50%)",
              border: "1.5px solid rgba(0,0,0,0.8)",
              borderRadius: "50%",
              boxShadow: "0 0 0 1px rgba(255,255,255,0.6)",
              pointerEvents: "none",
              zIndex: 30,
            }} />
          )}

          {/* Eraser active label */}
          {eraserActive && (
            <div className="absolute top-3 left-1/2 -translate-x-1/2 z-20 bg-black/70 text-white text-[10px] uppercase tracking-widest px-3 py-1 pointer-events-none">
              Eraser Active — click &amp; drag to erase
            </div>
          )}

          {/* Zoom controls — outside transform so they stay fixed */}
          <div className="absolute bottom-4 right-4 z-20 flex items-center gap-1 bg-white/90 border border-gray-200 shadow-sm px-2 py-1">
            <button
              onClick={() => { const rect = containerRef.current.getBoundingClientRect(); applyZoom(zoomRef.current - 0.5, rect.width / 2, rect.height / 2); }}
              disabled={zoom <= 1}
              className="p-1 hover:bg-gray-100 disabled:opacity-30 transition-colors"
              title="Zoom out"
            >
              <ZoomOut size={14} />
            </button>
            <span className="text-xs tabular-nums w-10 text-center text-gray-600 font-mono">
              {Math.round(zoom * 100)}%
            </span>
            <button
              onClick={() => { const rect = containerRef.current.getBoundingClientRect(); applyZoom(zoomRef.current + 0.5, rect.width / 2, rect.height / 2); }}
              disabled={zoom >= 8}
              className="p-1 hover:bg-gray-100 disabled:opacity-30 transition-colors"
              title="Zoom in"
            >
              <ZoomIn size={14} />
            </button>
            {zoom > 1 && (
              <button
                onClick={() => applyZoom(1, 0, 0)}
                className="ml-1 text-[10px] text-gray-500 hover:text-black border-l border-gray-200 pl-2 transition-colors"
                title="Reset zoom"
              >
                Reset
              </button>
            )}
          </div>
        </div>

        {/* Controls panel */}
        <div className={`border-l border-gray-200 flex flex-col overflow-y-auto flex-shrink-0 transition-all duration-200 ${panelOpen ? "w-72" : "w-0 border-l-0 overflow-hidden"}`}>
          <div className="p-5 space-y-5">
            <p className="text-xs font-medium uppercase tracking-widest text-gray-400">Controls</p>

            {/* Colour */}
            <div>
              <p className="text-[10px] uppercase tracking-widest text-gray-500 mb-2">Knockout Colour</p>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setEyedropperActive((v) => !v)}
                  title="Click image to sample colour"
                  className={`p-1.5 border transition-colors flex-shrink-0 ${eyedropperActive ? "bg-yellow-400 border-yellow-500" : "border-gray-200 hover:border-gray-400"}`}
                >
                  <Crosshair size={13} />
                </button>
                <div className="w-7 h-7 border border-gray-300 flex-shrink-0" style={{ background: knockoutColor.hex }} />
                <input
                  type="color"
                  value={knockoutColor.hex}
                  onChange={(e) => setKnockoutColor(hexToRgb(e.target.value))}
                  className="w-7 h-7 p-0 border-0 cursor-pointer flex-shrink-0"
                />
                <span className="text-[10px] font-mono text-gray-500">{knockoutColor.hex.toUpperCase()}</span>
              </div>
              {eyedropperActive && (
                <p className="text-[10px] text-yellow-600 font-medium mt-1.5">Click the image to sample a colour</p>
              )}
            </div>

            {/* Sliders */}
            <div className="space-y-4">
              {[
                { label: "Tolerance", value: knockoutTolerance, min: 10, max: 200, set: setKnockoutTolerance, unit: "", hint: "How broadly the colour is matched" },
                { label: "Dot Frequency", value: dotFrequency, min: 2, max: 20, set: setDotFrequency, unit: "", hint: "Halftone density for edges" },
                { label: "Dot Angle", value: dotAngle, min: 0, max: 90, set: setDotAngle, unit: "°", hint: "Rotation of the halftone grid" },
              ].map(({ label, value, min, max, set, unit, hint }) => (
                <div key={label}>
                  <div className="flex justify-between mb-1">
                    <label className="text-[10px] uppercase tracking-widest text-gray-500">{label}</label>
                    <span className="text-[10px] text-gray-700 tabular-nums font-medium">{value}{unit}</span>
                  </div>
                  <input type="range" min={min} max={max} value={value} onChange={(e) => set(+e.target.value)} className="w-full h-1 accent-black" />
                  <p className="text-[9px] text-gray-400 mt-0.5">{hint}</p>
                </div>
              ))}
            </div>

            {/* Pattern */}
            <div>
              <p className="text-[10px] uppercase tracking-widest text-gray-500 mb-2">Halftone Pattern</p>
              <div className="flex border border-gray-200">
                {["dots", "lines"].map((p) => (
                  <button
                    key={p}
                    onClick={() => setDotPattern(p)}
                    className={`flex-1 py-1.5 text-[10px] uppercase tracking-widest transition-colors ${dotPattern === p ? "bg-black text-white" : "bg-white text-gray-500 hover:bg-gray-50"}`}
                  >
                    {p}
                  </button>
                ))}
              </div>
            </div>

            {/* ── Pipeline adjustments ── */}
            <div className="pt-3 border-t border-gray-100">
              <p className="text-xs font-medium uppercase tracking-widest text-gray-400 mb-4">Adjustments</p>
              <div className="space-y-4">

                {/* Auto Cleanup */}
                <div className="border border-gray-200 p-3 space-y-3">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-[10px] uppercase tracking-widest text-gray-500 font-medium">Auto Cleanup</p>
                      <p className="text-[9px] text-gray-400 mt-0.5">Remove haze &amp; stray pixels</p>
                    </div>
                    <button
                      onClick={() => setAutoCleanup((v) => !v)}
                      className={`relative w-9 h-5 rounded-full transition-colors flex-shrink-0 ${autoCleanup ? "bg-black" : "bg-gray-300"}`}
                    >
                      <span className={`absolute top-0.5 w-4 h-4 bg-white rounded-full shadow transition-all ${autoCleanup ? "left-4" : "left-0.5"}`} />
                    </button>
                  </div>
                  {autoCleanup && (
                    <div>
                      <div className="flex justify-between mb-1">
                        <label className="text-[10px] uppercase tracking-widest text-gray-500">Strength</label>
                        <span className="text-[10px] text-gray-700 tabular-nums font-medium">{cleanupStrength}%</span>
                      </div>
                      <input type="range" min={10} max={100} value={cleanupStrength} onChange={(e) => setCleanupStrength(+e.target.value)} className="w-full h-1 accent-black" />
                    </div>
                  )}
                </div>

                {/* Eraser size (shown when eraser is active) */}
                {eraserActive && (
                  <div className="border border-yellow-300 bg-yellow-50 p-3 space-y-2">
                    <div className="flex justify-between mb-1">
                      <label className="text-[10px] uppercase tracking-widest text-gray-500">Eraser Size</label>
                      <span className="text-[10px] text-gray-700 tabular-nums font-medium">{eraserSize}px</span>
                    </div>
                    <input type="range" min={5} max={120} value={eraserSize} onChange={(e) => setEraserSize(+e.target.value)} className="w-full h-1 accent-black" />
                    <div className="flex items-center justify-between">
                      <p className="text-[9px] text-gray-400">Drag to erase • Ctrl+Z to undo</p>
                      <button onClick={clearErases} className="text-[9px] text-red-500 hover:text-red-700 underline transition-colors">Clear all</button>
                    </div>
                  </div>
                )}

                {/* Step 8: Edge Feather */}
                <div>
                  <div className="flex justify-between mb-1">
                    <label className="text-[10px] uppercase tracking-widest text-gray-500">Edge Feather</label>
                    <span className="text-[10px] text-gray-700 tabular-nums font-medium">{edgeFeather} px</span>
                  </div>
                  <input type="range" min={0} max={5} step={0.5} value={edgeFeather} onChange={(e) => setEdgeFeather(+e.target.value)} className="w-full h-1 accent-black" />
                  <p className="text-[9px] text-gray-400 mt-0.5">Soften cut edges (Step 8)</p>
                </div>

                {/* Step 2: Lightness */}
                <div>
                  <div className="flex justify-between mb-1">
                    <label className="text-[10px] uppercase tracking-widest text-gray-500">Lightness</label>
                    <span className="text-[10px] text-gray-700 tabular-nums font-medium">+{lightness}%</span>
                  </div>
                  <input type="range" min={0} max={30} value={lightness} onChange={(e) => setLightness(+e.target.value)} className="w-full h-1 accent-black" />
                  <p className="text-[9px] text-gray-400 mt-0.5">Clean up paper tone — brighten whites (Step 2)</p>
                </div>

                {/* Step 5: Yellow Reduction */}
                <div>
                  <div className="flex justify-between mb-1">
                    <label className="text-[10px] uppercase tracking-widest text-gray-500">Yellow Reduction</label>
                    <span className="text-[10px] text-gray-700 tabular-nums font-medium">{yellowReduction}</span>
                  </div>
                  <input type="range" min={0} max={40} value={yellowReduction} onChange={(e) => setYellowReduction(+e.target.value)} className="w-full h-1 accent-black" />
                  <p className="text-[9px] text-gray-400 mt-0.5">Remove yellow paper tone (Step 2 / 5)</p>
                </div>

                {/* Step 7: Red Boost */}
                <div>
                  <div className="flex justify-between mb-1">
                    <label className="text-[10px] uppercase tracking-widest text-gray-500">Red Boost</label>
                    <span className="text-[10px] text-gray-700 tabular-nums font-medium">+{redBoost}</span>
                  </div>
                  <input type="range" min={0} max={30} value={redBoost} onChange={(e) => setRedBoost(+e.target.value)} className="w-full h-1 accent-black" />
                  <p className="text-[9px] text-gray-400 mt-0.5">Strengthen reds for print (Step 7)</p>
                </div>

                {/* Steps 3 & 8: Grain */}
                <div>
                  <div className="flex justify-between mb-1">
                    <label className="text-[10px] uppercase tracking-widest text-gray-500">Grain</label>
                    <span className="text-[10px] text-gray-700 tabular-nums font-medium">{grain}%</span>
                  </div>
                  <input type="range" min={0} max={15} value={grain} onChange={(e) => setGrain(+e.target.value)} className="w-full h-1 accent-black" />
                  <p className="text-[9px] text-gray-400 mt-0.5">Overlay noise — gritty print finish (Steps 3 & 8)</p>
                </div>

              </div>
            </div>

            {/* Settings History */}
            {history.length > 0 && (
              <div className="pt-3 border-t border-gray-100">
                <button
                  onClick={() => setHistoryOpen((v) => !v)}
                  className="flex items-center justify-between w-full text-xs font-medium uppercase tracking-widest text-gray-400 mb-2"
                >
                  <span>History ({history.length})</span>
                  <span>{historyOpen ? "▲" : "▼"}</span>
                </button>
                {historyOpen && (
                  <div className="space-y-1">
                    {[...history].reverse().map((entry, i) => (
                      <div key={entry.ts} className="flex items-center justify-between py-1.5 px-2 border border-gray-100 hover:border-gray-300 transition-colors">
                        <span className="text-[10px] text-gray-500">{i === 0 ? "Latest — " : ""}{timeAgo(entry.ts)}</span>
                        <button
                          onClick={() => restoreSnapshot(entry.settings)}
                          className="text-[10px] text-black underline hover:no-underline transition-colors"
                        >
                          Restore
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
